import type { ElementType, ReactNode } from 'react';

/**
 * The shared Admin Settings card.
 *
 * Every pane under `/admin-settings/*` renders inside one of these so the section looks like
 * a single application rather than a dozen independently-styled screens. The card supplies the
 * matte surface, border, radius, and elevation; panes supply only their own content.
 *
 * This is deliberately thin. It owns the *frame* — surface, elevation, width, padding — and
 * nothing else, so a pane is free to lay out its interior however it needs. Reach for a custom
 * wrapper only if a pane genuinely cannot live in a card, and document why.
 *
 * Interior padding is **not** optional. There used to be a `padded={false}` escape hatch for
 * "panes that manage their own padding", and all eight panes that reached for it then declared
 * no padding at all — so their content sat flush against the card border. The hatch only ever
 * produced the bug it was meant to allow, so it is gone; `AdminPane.architecture.test.ts`
 * fails any pane that tries to reintroduce it.
 *
 * `AdminPane.architecture.test.ts` statically asserts every pane uses this, so a new pane that
 * forgets the card fails CI rather than silently shipping unstyled.
 */

/**
 * Width is a named intent rather than a raw value so panes stay visually consistent and a
 * future change to "what narrow means" lands everywhere at once.
 */
export const ADMIN_PANE_WIDTHS = {
  /** Settings forms: a short column of labelled controls. ~672px. */
  narrow: 'clr:max-w-2xl',
  /** The default. Mixed content — forms plus supporting copy or small lists. ~896px. */
  standard: 'clr:max-w-4xl',
  /** Content that needs room but still reads as a form, e.g. side-by-side fields. ~1152px. */
  wide: 'clr:max-w-6xl',
  /** Data tables and anything with inherently horizontal content. */
  full: 'clr:max-w-full',
} as const;

export type AdminPaneWidth = keyof typeof ADMIN_PANE_WIDTHS;

/**
 * The card treatment itself. Exported so tests and any unavoidable one-off can reference the
 * single definition instead of re-typing the class string.
 */
export const ADMIN_PANE_CARD_CLASSES =
  'prism-box clr:border clr:border-secondary-base clr:rounded-lg clr:shadow-sm-base clr:bg-surface-primary-base clr:text-primary-base';

type AdminPaneProps = {
  children: ReactNode;
  /** Defaults to `standard`. Use `full` for table-driven panes. */
  width?: AdminPaneWidth;
  /** Rendered element. Defaults to `section`, which is the correct landmark for a pane. */
  as?: ElementType;
  /** Extra classes for pane-specific layout. Appended last so they win. */
  className?: string;
  'data-testid'?: string;
};

export default function AdminPane({
  children,
  width = 'standard',
  as: Element = 'section',
  className = '',
  ...rest
}: AdminPaneProps) {
  const classes = [
    ADMIN_PANE_CARD_CLASSES,
    'clr:w-full clr:mx-auto',
    ADMIN_PANE_WIDTHS[width],
    'clr:p-lg',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Element className={classes} {...rest}>
      {children}
    </Element>
  );
}
